/**
 * @file ControlPanelPage.jsx
 * @description Control Panel — a dark "command centre" screen where the rental
 *              and service teams sit together and watch the whole fleet live.
 *              One aggregated API call (/stats/control-panel) feeds KPI tiles,
 *              charts, a live map, the alarm feed, active rentals and the
 *              service ticket queue. Auto-refreshes every 30 seconds.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    CircularProgress,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Search as SearchIcon,
    PrecisionManufacturing as FleetIcon,
    CarRental as RentalIcon,
    EventAvailable as AvailableIcon,
    Engineering as ServiceIcon,
    Wifi as OnlineIcon,
    Bolt as EngineIcon,
    NotificationsActive as AlarmIcon,
    ConfirmationNumber as TicketIcon,
    Circle as DotIcon,
    LocalGasStation as FuelIcon,
    Timer as HoursIcon,
    Shield as GeofenceIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import controlPanelService from '../../services/controlPanelService';
import { machineLabel } from '../../utils/machineLabel';
import ServiceControlPanel from './ServiceControlPanel';
import { useAuth } from '../../context/AuthContext';
import { userDepartments } from '../../utils/permissions';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, ChartTooltip, Legend);

const REFRESH_MS = 30000;

// ---- Palette — light, matching the app's MUI theme (theme.js) ----
const C = {
    bg: '#f8fafc',
    panel: '#ffffff',
    panelBorder: 'rgba(226, 232, 240, 0.8)',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    blue: '#2563eb',
    green: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    purple: '#8b5cf6',
    cyan: '#06b6d4'
};

const SEVERITY_COLOR = { critical: C.red, warning: C.amber, info: C.cyan };
// Fleet (rental) status → colour. On Rent = green; Service/Breakdown = amber;
// anything NOT on rent (available, de-hired, waiting) = blue; unknown = grey.
const STATUS_COLOR = {
    RENTAL: C.green, RENTED: C.green,
    SERVICE: C.amber, MAINTENANCE: C.amber, 'IN SERVICE': C.amber, BREAKDOWN: C.amber,
    AVAILABLE: C.blue, DE_HIRED: C.blue, 'DE-HIRED': C.blue, WAITING_FOR_RENT: C.blue,
    UNKNOWN: '#94a3b8',
};
const PRIORITY_COLOR = { CRITICAL: C.red, HIGH: '#f97316', MEDIUM: C.amber, LOW: C.green };

// ---- Small helpers ----
const timeAgo = (date) => {
    if (!date) return '—';
    const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

const daysLeft = (end) => {
    if (!end) return null;
    return Math.ceil((new Date(end).getTime() - Date.now()) / 86400000);
};

/** Shared panel container with title bar */
const Panel = ({ title, accent = C.blue, action, children, sx = {} }) => (
    <Box
        sx={{
            background: C.panel,
            border: `1px solid ${C.panelBorder}`,
            borderRadius: '14px',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...sx
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: `1px solid ${C.panelBorder}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 4, height: 16, borderRadius: 2, bgcolor: accent }} />
                <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase' }}>
                    {title}
                </Typography>
            </Box>
            {action}
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(148,163,184,0.25)', borderRadius: 3 } }}>
            {children}
        </Box>
    </Box>
);

/** Big KPI tile */
const KpiTile = ({ icon, label, value, sub, color, alert }) => (
    <Box
        sx={{
            position: 'relative',
            background: '#ffffff',
            border: `1px solid ${alert ? color : C.panelBorder}`,
            borderRadius: '14px',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            overflow: 'hidden',
            boxShadow: alert ? `0 0 18px ${color}33` : '0 1px 3px rgba(15, 23, 42, 0.06)',
            '&:after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: `linear-gradient(90deg, ${color}, transparent)`
            }
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color: C.textSecondary, fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Box sx={{ color, display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
        </Box>
        <Typography sx={{ color: C.textPrimary, fontSize: 32, fontWeight: 800, lineHeight: 1.1, fontFamily: '"Outfit", sans-serif' }}>
            {value ?? '—'}
        </Typography>
        {sub && (
            <Typography sx={{ color: C.textSecondary, fontSize: 11 }}>{sub}</Typography>
        )}
    </Box>
);

const ControlPanelPage = () => {
    const navigate = useNavigate();

    // Open a machine's detail page (same routing as Device Management / Hardware).
    const openDeviceDetail = (d) => {
        const id = d.equipment_id || d.device_id;
        if (!id) return;
        const cat = (d.device_type || '').toLowerCase();
        if (cat.includes('tower')) navigate(`/tower-lights/${id}`);
        else if (cat.includes('mewp') || cat === '') navigate(`/mewp-detail/${id}`);
        else navigate(`/devices/${id}`);
    };
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [clock, setClock] = useState(new Date());
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const containerRef = useRef(null);

    const fetchData = useCallback(async () => {
        setRefreshing(true);
        try {
            const snapshot = await controlPanelService.getSnapshot();
            setData(snapshot);
            setLastUpdated(new Date());
            setError(null);
        } catch (e) {
            setError('Live feed unavailable — retrying on next refresh.');
        } finally {
            setRefreshing(false);
        }
    }, []);

    // Initial load + auto refresh
    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, REFRESH_MS);
        return () => clearInterval(interval);
    }, [fetchData]);

    // Wall clock
    useEffect(() => {
        const t = setInterval(() => setClock(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // Fullscreen handling for the wall-mounted screen use case
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    };
    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const kpis = data?.kpis || {};
    const live = useMemo(() => data?.live || [], [data]);

    // Filtered fleet table rows
    const filteredLive = useMemo(() => {
        const q = search.trim().toLowerCase();
        return live.filter(d => {
            if (statusFilter === 'RENTAL' && !['RENTAL', 'RENTED'].includes(d.fleet_status)) return false;
            if (statusFilter === 'AVAILABLE' && ['RENTAL', 'RENTED', 'SERVICE', 'MAINTENANCE', 'IN SERVICE', 'BREAKDOWN'].includes(d.fleet_status)) return false;
            if (statusFilter === 'SERVICE' && !['SERVICE', 'MAINTENANCE', 'IN SERVICE', 'BREAKDOWN'].includes(d.fleet_status)) return false;
            if (statusFilter === 'ONLINE' && !d.online) return false;
            if (statusFilter === 'OFFLINE' && d.online) return false;
            if (!q) return true;
            return [d.serial_number, d.equipment_id, d.device_id, d.model, d.brand, d.customer, d.site, d.device_type]
                .some(v => v && String(v).toLowerCase().includes(q));
        });
    }, [live, search, statusFilter]);

    const mapDevices = useMemo(() => live.filter(d => d.lat && d.lon), [live]);
    const mapCenter = useMemo(() => {
        if (mapDevices.length === 0) return [20.59, 78.96]; // India
        const lat = mapDevices.reduce((s, d) => s + d.lat, 0) / mapDevices.length;
        const lon = mapDevices.reduce((s, d) => s + d.lon, 0) / mapDevices.length;
        return [lat, lon];
    }, [mapDevices]);

    // ---- Charts ----
    const fleetStatusChart = {
        labels: ['On Rent', 'Available', 'In Service'],
        datasets: [{
            data: [kpis.onRent || 0, kpis.available || 0, kpis.inService || 0],
            backgroundColor: [C.blue, C.green, C.amber],
            borderColor: C.bg,
            borderWidth: 3,
            hoverOffset: 6
        }]
    };

    const fleetByType = data?.fleetByType || [];
    const typeChart = {
        labels: fleetByType.map(t => t.type),
        datasets: [
            { label: 'Total', data: fleetByType.map(t => t.total), backgroundColor: 'rgba(148,163,184,0.35)', borderRadius: 6 },
            { label: 'Online', data: fleetByType.map(t => t.online), backgroundColor: C.cyan, borderRadius: 6 },
            { label: 'On Rent', data: fleetByType.map(t => t.onRent), backgroundColor: C.blue, borderRadius: 6 }
        ]
    };

    const darkChartOpts = (showLegend = true) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: showLegend
                ? { position: 'bottom', labels: { color: C.textSecondary, boxWidth: 10, boxHeight: 10, font: { size: 10 } } }
                : { display: false },
            tooltip: { backgroundColor: '#1e293b', borderColor: C.panelBorder, borderWidth: 1 }
        },
        scales: undefined
    });

    const barOpts = {
        ...darkChartOpts(),
        scales: {
            x: { ticks: { color: C.textSecondary, font: { size: 10 } }, grid: { display: false } },
            y: { ticks: { color: C.textSecondary, font: { size: 10 } }, grid: { color: 'rgba(148,163,184,0.08)' } }
        }
    };

    if (!data && !error) {
        return (
            <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, background: C.bg, borderRadius: '14px' }}>
                <CircularProgress sx={{ color: C.blue }} />
                <Typography sx={{ color: C.textSecondary }}>Connecting to live fleet feed…</Typography>
            </Box>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                background: C.bg,
                borderRadius: isFullscreen ? 0 : '14px',
                p: { xs: 1.5, md: 2.5 },
                minHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            {/* ---------- Toolbar ---------- */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.4, borderRadius: 99, bgcolor: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)' }}>
                    <DotIcon sx={{ fontSize: 10, color: C.green, animation: 'cpPulse 1.6s ease-in-out infinite', '@keyframes cpPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                    <Typography sx={{ color: C.green, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>LIVE</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: 18, fontFamily: 'monospace' }}>
                        {clock.toLocaleTimeString('en-IN', { hour12: false })}
                    </Typography>
                    <Typography sx={{ color: C.textSecondary, fontSize: 11 }}>
                        Updated {lastUpdated ? timeAgo(lastUpdated) : '—'}
                    </Typography>
                    <Tooltip title="Refresh now">
                        <IconButton size="small" onClick={fetchData} sx={{ color: C.textSecondary }}>
                            <RefreshIcon sx={{ fontSize: 18, animation: refreshing ? 'cpSpin 1s linear infinite' : 'none', '@keyframes cpSpin': { to: { transform: 'rotate(360deg)' } } }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen (wall display)'}>
                        <IconButton size="small" onClick={toggleFullscreen} sx={{ color: C.textSecondary }}>
                            {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && <Alert severity="warning" sx={{ borderRadius: '10px' }}>{error}</Alert>}

            {/* ---------- KPI tiles ---------- */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(8, 1fr)' }, gap: 1.5 }}>
                <KpiTile icon={<FleetIcon />} label="Total Fleet" value={kpis.totalFleet} color={C.purple} sub="All tracked assets" />
                <KpiTile icon={<RentalIcon />} label="On Rent" value={kpis.onRent} color={C.blue} sub={`${kpis.utilizationRate ?? 0}% utilisation`} />
                <KpiTile icon={<AvailableIcon />} label="Available" value={kpis.available} color={C.green} sub="Ready" />
                <KpiTile icon={<ServiceIcon />} label="In Service" value={kpis.inService} color={C.amber} sub="Workshop / maintenance" />
                <KpiTile icon={<OnlineIcon />} label="Online Now" value={kpis.online} color={C.cyan} sub="Reported < 30 min" />
                <KpiTile icon={<EngineIcon />} label="Engines On" value={kpis.engineOn} color={C.green} sub="Running right now" />
                <KpiTile icon={<AlarmIcon />} label="Open Alarms" value={kpis.openAlarms} color={C.red} alert={(kpis.criticalAlarms || 0) > 0} sub={`${kpis.criticalAlarms || 0} critical`} />
                <KpiTile icon={<TicketIcon />} label="Open Tickets" value={kpis.openTickets} color={C.amber} sub={`${kpis.expiringContracts || 0} contracts ending ≤ 7d`} />
            </Box>

            {/* ---------- Charts + Map ---------- */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1.3fr 2fr' }, gap: 1.5, minHeight: 280 }}>
                <Panel title="Fleet Status" accent={C.blue}>
                    <Box sx={{ position: 'relative', height: 230, p: 2 }}>
                        <Doughnut data={fleetStatusChart} options={{ ...darkChartOpts(), cutout: '68%' }} />
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', pb: 4 }}>
                            <Typography sx={{ color: C.textPrimary, fontSize: 28, fontWeight: 800 }}>{kpis.utilizationRate ?? 0}%</Typography>
                            <Typography sx={{ color: C.textSecondary, fontSize: 10, letterSpacing: 1 }}>UTILISATION</Typography>
                        </Box>
                    </Box>
                </Panel>

                <Panel title="Fleet by Equipment Type" accent={C.cyan}>
                    <Box sx={{ height: 230, p: 2 }}>
                        <Bar data={typeChart} options={barOpts} />
                    </Box>
                </Panel>

                <Panel
                    title={`Live Fleet Map — ${mapDevices.length} positioned`}
                    accent={C.green}
                    action={
                        <Box sx={{ display: 'flex', gap: 1.2 }}>
                            {[['On Rent', C.green], ['Available', C.blue], ['Service', C.amber]].map(([l, c]) => (
                                <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <DotIcon sx={{ fontSize: 9, color: c }} />
                                    <Typography sx={{ color: C.textSecondary, fontSize: 10 }}>{l}</Typography>
                                </Box>
                            ))}
                        </Box>
                    }
                >
                    <Box sx={{ height: 230, '& .leaflet-container': { height: '100%', width: '100%', background: '#e2e8f0' } }}>
                        <MapContainer center={mapCenter} zoom={5} scrollWheelZoom style={{ height: '100%' }}>
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; OSM'
                            />
                            {mapDevices.map(d => (
                                <CircleMarker
                                    key={d.device_id || d.equipment_id}
                                    center={[d.lat, d.lon]}
                                    radius={d.engine_on ? 8 : 6}
                                    pathOptions={{
                                        // Unmapped status = NOT on rent → blue (never default to green)
                                        color: STATUS_COLOR[d.fleet_status] || C.blue,
                                        fillColor: STATUS_COLOR[d.fleet_status] || C.blue,
                                        fillOpacity: d.online ? 0.9 : 0.35,
                                        weight: d.engine_on ? 3 : 1.5
                                    }}
                                >
                                    <Popup>
                                        <b>{machineLabel(d)}</b><br />
                                        {d.brand} {d.model}<br />
                                        Status: {d.fleet_status}<br />
                                        {d.customer ? <>Customer: {d.customer}<br /></> : null}
                                        Engine: {d.engine_on ? 'ON' : 'Off'} · {d.engine_hours != null ? `${d.engine_hours} h` : '—'}<br />
                                        Last seen: {timeAgo(d.last_seen)}
                                        <span
                                            role="button"
                                            onClick={() => openDeviceDetail(d)}
                                            style={{ display: 'inline-block', marginTop: 8, color: '#2563eb', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                                        >
                                            View details &amp; live data →
                                        </span>
                                    </Popup>
                                </CircleMarker>
                            ))}
                        </MapContainer>
                    </Box>
                </Panel>
            </Box>

            {/* ---------- Ops panels: alarms / rentals / service ---------- */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1.1fr 1.5fr 1.4fr' }, gap: 1.5, minHeight: 300 }}>
                {/* Live alarm feed */}
                <Panel
                    title={`Live Alarms (${kpis.openAlarms || 0})`}
                    accent={C.red}
                    action={
                        <Chip
                            size="small"
                            label="View all"
                            onClick={() => navigate('/alarms')}
                            sx={{ bgcolor: 'rgba(239,68,68,0.12)', color: C.red, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}
                        />
                    }
                >
                    <Box sx={{ maxHeight: 300 }}>
                        {(data?.alarms?.recent || []).length === 0 && (
                            <Typography sx={{ color: C.textSecondary, fontSize: 12, p: 2, textAlign: 'center' }}>
                                No open alarms
                            </Typography>
                        )}
                        {(data?.alarms?.recent || []).map(a => (
                            <Box key={a.id} sx={{ display: 'flex', gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${C.panelBorder}`, '&:hover': { background: 'rgba(148,163,184,0.05)' } }}>
                                <DotIcon sx={{ fontSize: 10, mt: 0.5, color: SEVERITY_COLOR[(a.severity || 'warning').toLowerCase()] || C.amber }} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography sx={{ color: C.textPrimary, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {machineLabel(a, a.contract_id || 'System')} · {a.alarm_name}
                                        </Typography>
                                        <Typography sx={{ color: C.textSecondary, fontSize: 10, whiteSpace: 'nowrap' }}>{timeAgo(a.created_at)}</Typography>
                                    </Box>
                                    {a.message && (
                                        <Typography sx={{ color: C.textSecondary, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {a.message}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Panel>

                {/* Rental team panel */}
                <Panel
                    title={`Active Rentals (${kpis.activeContracts || 0})`}
                    accent={C.blue}
                    action={
                        <Chip size="small" label="Rentals" onClick={() => navigate('/rentals')} sx={{ bgcolor: 'rgba(59,130,246,0.12)', color: C.blue, fontSize: 10, fontWeight: 700, cursor: 'pointer' }} />
                    }
                >
                    <TableContainer sx={{ maxHeight: 300 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {['Customer / Site', 'Units', 'Ends', 'Fence'].map(h => (
                                        <TableCell key={h} sx={{ background: '#f1f5f9', color: C.textSecondary, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, borderBottom: `1px solid ${C.panelBorder}`, py: 0.8 }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data?.rentals?.active || []).map(rc => {
                                    const dl = daysLeft(rc.end);
                                    return (
                                        <TableRow
                                            key={rc.contract_id}
                                            hover
                                            onClick={() => navigate(`/contract-live/${rc.contract_id}`)}
                                            sx={{ cursor: 'pointer', '& td': { borderBottom: `1px solid ${C.panelBorder}`, py: 0.7 } }}
                                        >
                                            <TableCell sx={{ maxWidth: 180 }}>
                                                <Typography sx={{ color: C.textPrimary, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {rc.customer || rc.contract_id}
                                                </Typography>
                                                <Typography sx={{ color: C.textSecondary, fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {rc.site || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ color: C.textPrimary, fontSize: 12, fontWeight: 700 }}>{rc.equipment_count}</TableCell>
                                            <TableCell>
                                                {dl === null ? (
                                                    <Typography sx={{ color: C.textSecondary, fontSize: 11 }}>Open</Typography>
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        label={dl < 0 ? 'Overdue' : `${dl}d`}
                                                        sx={{
                                                            height: 18,
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                            bgcolor: dl <= 3 ? 'rgba(239,68,68,0.15)' : dl <= 7 ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.12)',
                                                            color: dl <= 3 ? C.red : dl <= 7 ? C.amber : C.green
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <GeofenceIcon sx={{ fontSize: 14, color: rc.geofence ? C.green : 'rgba(148,163,184,0.3)' }} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {(data?.rentals?.active || []).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ color: C.textSecondary, fontSize: 12, textAlign: 'center', borderBottom: 'none' }}>
                                            No active contracts
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Panel>

                {/* Service team panel */}
                <Panel
                    title={`Service Queue (${kpis.openTickets || 0})`}
                    accent={C.amber}
                    action={
                        <Chip size="small" label="Service" onClick={() => navigate('/service')} sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: C.amber, fontSize: 10, fontWeight: 700, cursor: 'pointer' }} />
                    }
                >
                    <Box sx={{ maxHeight: 300 }}>
                        {(data?.service?.queue || []).length === 0 && (
                            <Typography sx={{ color: C.textSecondary, fontSize: 12, p: 2, textAlign: 'center' }}>
                                No open tickets
                            </Typography>
                        )}
                        {(data?.service?.queue || []).map(t => (
                            <Box
                                key={t.id}
                                onClick={() => navigate(`/service/tickets/${t.id}`)}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${C.panelBorder}`, cursor: 'pointer', '&:hover': { background: 'rgba(148,163,184,0.05)' } }}
                            >
                                <Chip
                                    size="small"
                                    label={(t.priority || 'MED').toUpperCase().slice(0, 4)}
                                    sx={{
                                        height: 18,
                                        fontSize: 9,
                                        fontWeight: 800,
                                        minWidth: 44,
                                        bgcolor: `${PRIORITY_COLOR[(t.priority || 'MEDIUM').toUpperCase()] || C.amber}22`,
                                        color: PRIORITY_COLOR[(t.priority || 'MEDIUM').toUpperCase()] || C.amber
                                    }}
                                />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ color: C.textPrimary, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        #{t.id} · {machineLabel(t)} · {t.issue_type || 'Issue'}
                                    </Typography>
                                    <Typography sx={{ color: C.textSecondary, fontSize: 10 }}>
                                        {t.technician || 'Unassigned'} · {t.status} · {timeAgo(t.created_at)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Panel>
            </Box>

            {/* ---------- Live fleet table ---------- */}
            <Panel
                title={`Live Fleet (${filteredLive.length} of ${live.length})`}
                accent={C.purple}
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {['ALL', 'RENTAL', 'AVAILABLE', 'SERVICE', 'ONLINE', 'OFFLINE'].map(f => (
                            <Chip
                                key={f}
                                size="small"
                                label={f === 'RENTAL' ? 'ON RENT' : f}
                                onClick={() => setStatusFilter(f)}
                                sx={{
                                    height: 20,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    bgcolor: statusFilter === f ? 'rgba(139,92,246,0.12)' : 'rgba(148,163,184,0.08)',
                                    color: statusFilter === f ? '#6d28d9' : C.textSecondary,
                                    border: statusFilter === f ? '1px solid rgba(139,92,246,0.5)' : `1px solid ${C.panelBorder}`
                                }}
                            />
                        ))}
                        <TextField
                            size="small"
                            placeholder="Search fleet…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 16, color: C.textSecondary }} />
                                    </InputAdornment>
                                ),
                                sx: { color: C.textPrimary, fontSize: 12, height: 30, background: 'rgba(148,163,184,0.06)', borderRadius: '8px', '& fieldset': { borderColor: C.panelBorder } }
                            }}
                        />
                    </Box>
                }
            >
                <TableContainer sx={{ maxHeight: 380 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {['Serial No.', 'Type', 'Status', 'Link', 'Engine', 'Hours', 'Fuel', 'Customer / Site', 'Last Seen'].map(h => (
                                    <TableCell key={h} sx={{ background: '#f1f5f9', color: C.textSecondary, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, borderBottom: `1px solid ${C.panelBorder}`, py: 0.8 }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLive.map(d => (
                                <TableRow
                                    key={d.device_id || d.equipment_id}
                                    hover
                                    onClick={() => d.device_id && navigate(`/devices/${d.device_id}`)}
                                    sx={{ cursor: 'pointer', '& td': { borderBottom: `1px solid ${C.panelBorder}`, py: 0.6, color: C.textPrimary, fontSize: 12 } }}
                                >
                                    <TableCell>
                                        <Typography sx={{ color: C.textPrimary, fontSize: 12, fontWeight: 700 }}>{machineLabel(d)}</Typography>
                                        <Typography sx={{ color: C.textSecondary, fontSize: 10 }}>{d.brand} {d.model}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: `${C.textSecondary} !important` }}>{d.device_type}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={d.fleet_status}
                                            sx={{ height: 18, fontSize: 9, fontWeight: 800, bgcolor: `${STATUS_COLOR[d.fleet_status] || C.blue}22`, color: STATUS_COLOR[d.fleet_status] || C.blue }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <DotIcon sx={{ fontSize: 10, color: d.online ? C.green : 'rgba(148,163,184,0.3)' }} />
                                    </TableCell>
                                    <TableCell>
                                        {d.engine_on
                                            ? <Chip size="small" icon={<EngineIcon sx={{ fontSize: '12px !important' }} />} label="ON" sx={{ height: 18, fontSize: 9, fontWeight: 800, bgcolor: 'rgba(34,197,94,0.15)', color: C.green }} />
                                            : <Typography sx={{ color: C.textSecondary, fontSize: 11 }}>Off</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <HoursIcon sx={{ fontSize: 12, color: C.textSecondary }} />
                                            {d.engine_hours != null ? `${Number(d.engine_hours).toLocaleString()} h` : '—'}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {d.fuel_level != null ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <FuelIcon sx={{ fontSize: 12, color: d.fuel_level < 20 ? C.red : C.textSecondary }} />
                                                <Typography sx={{ fontSize: 11, color: d.fuel_level < 20 ? C.red : C.textPrimary, fontWeight: d.fuel_level < 20 ? 700 : 400 }}>
                                                    {d.fuel_level}%
                                                </Typography>
                                            </Box>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        {d.customer ? (
                                            <>
                                                <Typography sx={{ color: C.textPrimary, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.customer}</Typography>
                                                <Typography sx={{ color: C.textSecondary, fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.site || ''}</Typography>
                                            </>
                                        ) : <Typography sx={{ color: C.textSecondary, fontSize: 11 }}>—</Typography>}
                                    </TableCell>
                                    <TableCell sx={{ color: `${C.textSecondary} !important`, fontSize: '11px !important', whiteSpace: 'nowrap' }}>
                                        {timeAgo(d.last_seen)}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredLive.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ color: C.textSecondary, fontSize: 12, textAlign: 'center', borderBottom: 'none', py: 3 }}>
                                        No devices match the current filter
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Panel>
        </Box>
    );
};

/**
 * Department router for /control-panel. Each department's work is different,
 * so each gets its own panel: the Service header links ?dept=service and a
 * service-only user lands there by default. Everyone else keeps this shared
 * fleet panel until their department's own is built.
 *
 * It is a wrapper, not a branch inside ControlPanelPage, so neither panel's
 * hooks are ever called conditionally.
 */
const ControlPanelEntry = () => {
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const dept = (searchParams.get('dept') || '').toLowerCase();
    const serviceOnly = userDepartments(user).join(',') === 'service';
    if (dept === 'service' || (!dept && serviceOnly)) return <ServiceControlPanel />;
    return <ControlPanelPage />;
};

export default ControlPanelEntry;
// The shared fleet panel, for department page files that want it directly
// (Sales / Operations control panels) without the ?dept= entry logic.
export { ControlPanelPage as FleetControlPanel };
